import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import AgoraUIKit from "agora-react-uikit";
import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";

import Logo1 from "../Logo/Dark/Icon - Copy/1Asset 15@72x-8.png";
import Logo from "../Logo/Dark/Full - Copy/1Asset 17@72x-8.png";


// Placeholder for Agora Chat components if you want to integrate Agora RTM Chat SDK
// import { UIKitProvider, ConversationList, Chat } from 'agora-react-uikit-chat';

const AgoraSessionPage = () => {
  const [params] = useSearchParams();
  const appId = params.get("appId");
  const token = params.get("token");
  const channel = params.get("channel");
  const uid = params.get("uid") || null;
  const role = params.get("role") || "subscriber"; // publisher or subscriber

  const [inCall, setInCall] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Role for AgoraUIKit: "host" or "audience"
  const rtcRole = role === "publisher" ? "host" : "audience";

  const rtcProps = {
    appId,
    channel,
    token,
    uid,
    role: rtcRole,
    layout: 1,
    enableScreenshare: rtcRole === "host",
  };

  // To programmatically toggle screen sharing through ref if needed
  const agoraUIKitRef = useRef();

  // Callbacks from AgoraUIKit
  const callbacks = {
    EndCall: () => setInCall(false),
    // Custom callback to toggle screen share state inside the component
    OnScreenShareToggle: () => setScreenSharing((prev) => !prev),
  };

  // Style overrides for AgoraUIKit
  const styleProps = {
    UIKitContainer: {
      height: "100vh",
      backgroundColor: theme.palette.mode === "dark" ? "#121212" : "#f9f9f9",
    },
    localBtnContainer: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 14,
      padding: isMobile ? 6 : 12,
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 2px 8px rgba(0,0,0,0.8)"
          : "0 2px 8px rgba(0,0,0,0.1)",
    },
    localBtn: {
      backgroundColor: theme.palette.primary.main,
      color: "#fff",
      borderRadius: 10,
      fontWeight: 600,
    },
    remoteBtnContainer: {
      top: 8,
    },
  };

  return inCall ? (
    <Box
      sx={{
        position: "relative",
        height: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Left/main video + controls */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          height: isMobile ? "60vh" : "100vh",
          width: "100%",
        }}
      >
        {/* Top Left Logo */}
        <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 100 }}>
          <img
            src={isMobile ? Logo1 : Logo}
            alt="Logo"
            style={{ width: isMobile ? 50 : 140 }}
          />
        </Box>

        {/* Top Right Controls */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 100,
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          {/* Screen Share Toggle (only for host) */}
          {rtcRole === "host" && (
            <Tooltip title={screenSharing ? "Stop Screen Share" : "Start Screen Share"}>
              <IconButton
                color={screenSharing ? "secondary" : "primary"}
                onClick={() => {
                  if (agoraUIKitRef.current) {
                    agoraUIKitRef.current.methods
                      .toggleScreenshare()
                      .then(() => setScreenSharing(!screenSharing))
                      .catch(() => {});
                  }
                }}
                size={isMobile ? "small" : "medium"}
              >
                {screenSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
              </IconButton>
            </Tooltip>
          )}

          {/* Chat Toggle */}
          <Tooltip title={chatOpen ? "Close Chat" : "Open Chat"}>
            <IconButton
              color={chatOpen ? "secondary" : "primary"}
              onClick={() => setChatOpen((open) => !open)}
              size={isMobile ? "small" : "medium"}
            >
              {chatOpen ? <CloseIcon /> : <ChatIcon />}
            </IconButton>
          </Tooltip>

          {/* Leave Call */}
          <Button
            variant="contained"
            color="error"
            startIcon={<ExitToAppIcon />}
            onClick={() => setInCall(false)}
            size={isMobile ? "small" : "medium"}
          >
            Leave
          </Button>
        </Box>

        {/* Agora UIKit Video */}
        <AgoraUIKit
          ref={agoraUIKitRef}
          rtcProps={rtcProps}
          callbacks={callbacks}
          styleProps={styleProps}
        />
      </Box>

      {/* Chat panel */}
      {chatOpen && (
        <Box
          sx={{
            width: isMobile ? "100%" : 320,
            height: isMobile ? "40vh" : "100vh",
            bgcolor: theme.palette.background.paper,
            borderLeft: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
            borderTop: isMobile ? `1px solid ${theme.palette.divider}` : "none",
            boxShadow: "-2px 0 8px rgba(0,0,0,0.1)",
            position: isMobile ? "relative" : "relative",
            zIndex: 50,
            overflowY: "auto",
          }}
        >
          {/* 
            TODO: Integrate Agora RTM Chat UI here. 
            Placeholder for chat UI:
          */}
          <Box
            sx={{
              p: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: theme.palette.text.secondary,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Chat Panel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Integrate Agora RTM Chat SDK here.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  ) : (
    // After leaving session UI
    <Box
      sx={{
        height: "100vh",
        bgcolor: theme.palette.background.default,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        textAlign: "center",
        px: 2,
      }}
    >
      <img src={isMobile ? Logo1 : Logo} alt="Logo" style={{ width: isMobile ? 60 : 160 }} />
      <Typography variant="h5" mt={3} gutterBottom>
        You have left the session.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        onClick={() => setInCall(true)}
      >
        Rejoin
      </Button>
    </Box>
  );
};

export default AgoraSessionPage;
